import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calculator, LineChart, Users, Zap, Activity, ArrowRight, Loader2 } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

const TOOL_META = {
    cost: { label: "Maliyet Tahmini", href: "/araclar/maliyet-hesaplayici", Icon: Calculator, cta: "Maliyet Hesapla" },
    roi: { label: "ROI Tahmini", href: "/araclar/roi-hesaplayici", Icon: LineChart, cta: "ROI Hesapla" },
    staff: { label: "Personel Planı", href: "/araclar/personel-planlayici", Icon: Users, cta: "Personel Planla" },
    energy: { label: "Enerji Tahmini", href: "/araclar/enerji-hesaplayici", Icon: Zap, cta: "Enerji Hesapla" },
    occupancy: { label: "Doluluk Tahmini", href: "/araclar/doluluk-tahmincisi", Icon: Activity, cta: "Doluluk Hesapla" },
};

function statsForTool(tool, summary) {
    if (tool === "cost") {
        return [
            { label: "Toplam Tahmin", value: summary.total_cost, suffix: " ₺" },
            { label: "Oda Başı", value: summary.cost_per_room, suffix: " ₺" },
            { label: "Geri Dönüş", value: summary.estimated_roi_years, suffix: " yıl", decimals: 1 },
            { label: `${summary.star}★ · ${summary.room_count} oda`, value: null, extra: summary.city_name },
        ];
    }
    if (tool === "roi") {
        return [
            { label: "Geri Dönüş", value: summary.payback_period_years, suffix: " yıl", decimals: 1 },
            { label: "Yıllık Net Kâr", value: summary.annual_net_profit, suffix: " ₺" },
            { label: "Toplam Yatırım", value: summary.total_investment, suffix: " ₺" },
            { label: "Yıllık Gelir", value: summary.annual_revenue, suffix: " ₺" },
        ];
    }
    if (tool === "staff") {
        return [
            { label: "Toplam Personel", value: summary.total_staff, suffix: " kişi" },
            { label: "Oda Başı", value: summary.staff_per_room, suffix: "", decimals: 2 },
            { label: "Aylık Bordro", value: summary.monthly_payroll, suffix: " ₺" },
            { label: "Yıllık Bordro", value: summary.annual_payroll, suffix: " ₺" },
        ];
    }
    if (tool === "energy") {
        return [
            { label: "Yıllık Maliyet", value: summary.annual_cost, suffix: " ₺" },
            { label: "Yıllık kWh", value: summary.annual_kwh, suffix: " kWh" },
            { label: "CO₂ Salınımı", value: summary.co2_tons_year, suffix: " ton/yıl", decimals: 1 },
            { label: `${summary.star}★ · ${summary.room_count} oda`, value: null, extra: "" },
        ];
    }
    if (tool === "occupancy") {
        return [
            { label: "Yıllık Doluluk", value: summary.annual_avg_occupancy_pct, suffix: " %", decimals: 1 },
            { label: "RevPAR", value: summary.annual_revpar, suffix: " ₺" },
            { label: "Ağırlıklı ADR", value: summary.weighted_adr, suffix: " ₺" },
            { label: `${summary.star}★`, value: null, extra: summary.city_name },
        ];
    }
    return [];
}

export default function ShareResult() {
    const { id } = useParams();
    const nav = useNavigate();
    const [share, setShare] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get(`/share/${id}`);
                setShare(data);
            } catch (e) {
                setErr(e?.response?.status === 404 ? "Paylaşım bulunamadı" : "Yüklenemedi");
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    const origin = (typeof window !== "undefined" && window.location.origin) || "";
    const ogImage = `${origin}/api/share/${id}/og.png`;

    useSeo({
        title: share?.title || "Hesaplama",
        description: share?.description || "Otel Pro hesaplama sonucu",
        path: `/sonuc/${id}`,
        ogImage,
    });

    const tool = share?.tool_type;
    const meta = TOOL_META[tool] || TOOL_META.cost;
    const summary = share?.summary || {};
    const stats = tool ? statsForTool(tool, summary) : [];
    const Icon = meta.Icon;

    return (
        <div className="min-h-[80vh] bg-light-bg" data-testid="share-result-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-4xl mx-auto px-6 py-12 lg:py-16 text-center">
                    {loading ? (
                        <div className="flex items-center justify-center gap-2 text-white/80">
                            <Loader2 className="w-5 h-5 animate-spin" /> Yükleniyor...
                        </div>
                    ) : err ? (
                        <div className="text-center">
                            <h1 className="text-3xl font-bold">{err}</h1>
                            <Link to="/araclar" className="mt-4 inline-flex items-center gap-2 text-teal hover:text-white">
                                Araçlara dön <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                    ) : (
                        <>
                            <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">{meta.label}</span>
                            <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">{share.title}</h1>
                            <p className="mt-3 text-white/75 max-w-xl mx-auto">{share.description}</p>
                        </>
                    )}
                </div>
            </section>

            {!loading && !err && (
                <section className="max-w-4xl mx-auto px-6 -mt-10 relative z-10">
                    <img
                        src={ogImage}
                        alt={share.title}
                        className="w-full rounded-lg border border-slate-200 shadow-2xl"
                        data-testid="share-image"
                    />

                    <div className="mt-8 grid sm:grid-cols-2 gap-4">
                        {stats.map((s, i) => (
                            <Stat key={i} idx={i} {...s} />
                        ))}
                    </div>

                    <div className="mt-10 bg-dark-navy text-white rounded-lg p-7 text-center">
                        <h2 className="text-2xl font-bold">Kendi hesaplamanı yap</h2>
                        <p className="mt-2 text-white/75 max-w-xl mx-auto">
                            Sen de kendi senaryonu Otel Pro araçlarıyla saniyeler içinde hesapla, PDF rapor indir.
                        </p>
                        <button
                            onClick={() => nav(meta.href)}
                            className="mt-5 inline-flex items-center gap-2 bg-gold hover:bg-gold-700 text-dark-navy font-bold text-sm px-6 py-3 rounded-md shadow-md"
                            data-testid="share-cta-calculate"
                        >
                            <Icon className="w-4 h-4" />
                            {meta.cta} <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </section>
            )}
        </div>
    );
}

function Stat({ label, value, suffix = "", decimals = 0, extra, idx = 0 }) {
    const display =
        value == null
            ? extra || "—"
            : `${Number(value).toLocaleString("tr-TR", { maximumFractionDigits: decimals })}${suffix}`;
    return (
        <div className="bg-white rounded-lg border border-slate-100 shadow-md p-5" data-testid={`share-stat-${idx}`}>
            <div className="text-xs uppercase tracking-wider text-deep-blue/70 font-semibold">{label}</div>
            <div className="mt-1 font-data text-2xl font-bold text-dark-navy">{display}</div>
        </div>
    );
}
